import { html } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_DEVICE_CONTROL_EXCLUSIONS } from '../../../../../../../webcomponents.constants';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import {
  DeviceControlExclusionsFormValue,
  DeviceControlExclusionsSearchableKeywords,
} from '../../../../utils/uiSettings/deviceControl/device-control.types';
import { DeviceControlExclusionRuleItem, ModuleId, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import {
  getDeviceControlExceptionsDefaultValue,
  getDeviceControlExclusionsSearchableKeywords,
} from '../../../../utils/uiSettings/deviceControl/device-control.values';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { toggleDisabled } from '../../../../../../../utils/htmlElement.utils';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import _ from 'lodash';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { GzDeviceControlExclusionsGridWebComponent } from '../gridComponents/GzDeviceControlExclusionsGridWebComponent';

@customElement(WEB_COMPONENT_POLICY_PANEL_DEVICE_CONTROL_EXCLUSIONS)
export class DeviceControlExclusionsWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  DeviceControlExclusionsFormValue,
  DeviceControlExclusionsSearchableKeywords
> {
  static get styles() {
    return GzPolicyPanelAccordionTemplateWebComponent.styles;
  }

  protected path = {
    moduleId: ModuleId.DEVICE_CONTROL,
    sectionId: SectionId.EXCLUSIONS,
  };
  protected searchableKeywords = getDeviceControlExclusionsSearchableKeywords();

  @query('#device-control-exclusions-toggle')
  private exclusionsToggle: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;

  @query('#exclusions-grid-form-field')
  private exclusionsGridFormField: GzFormFieldWebComponent<
    GzDeviceControlExclusionsGridWebComponent,
    DeviceControlExclusionRuleItem[]
  >;

  @query('#device-control-exclusions-content')
  private exclusionsContent: HTMLElement;

  protected renderTitle() {
    return html`<gz-form-field id="device-control-exclusions-toggle">
      <gz-toggle
        class="content"
        searchable-label="${this.searchableKeywords[DeviceControlExclusionsSearchableKeywords.Exclusions].label}"
        gz-form-input
        @toggleChange="${this.onToggleChange}"
      >
        <span>${this.searchableKeywords[DeviceControlExclusionsSearchableKeywords.Exclusions].translatedLabel}</span>
      </gz-toggle>
    </gz-form-field>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html``;
  }

  protected renderContent() {
    return html`<div id="device-control-exclusions-content" class="width-100">
      <gz-form-field id="exclusions-grid-form-field">
        <gz-policy-panel-device-control-exclusions-grid gz-form-input> </gz-policy-panel-device-control-exclusions-grid>
      </gz-form-field>
    </div>`;
  }

  protected createFormGroup(): void {
    this.formGroup = FormGroup.build({
      uiSettings: {
        deviceControl: {
          dataLossPreventionExceptions: {
            enable: this.exclusionsToggle,
            rules: this.exclusionsGridFormField,
          },
        },
      },
    });
  }

  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }
    const value = _.cloneDeep(this.formGroup.value);

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'deviceControl', 'dataLossPreventionExceptions'], {});
    }
    return value;
  }

  protected getValueFromManager(): DeviceControlExclusionsFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [ModuleId.DEVICE_CONTROL]: {
          dataLossPreventionExceptions: getDeviceControlExceptionsDefaultValue(),
        },
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.DEVICE_CONTROL]: {
          dataLossPreventionExceptions: PolicyPanelManager.getInstance().getPolicy(
            'uiSettings.deviceControl.dataLossPreventionExceptions'
          ),
        },
      },
    };

    return deepmerge(defaultValue, value);
  }

  protected setFormData(): void {
    if (!this.valueT) {
      return;
    }
    // set form group value
    this.formGroup.value = {
      uiSettings: {
        deviceControl: {
          dataLossPreventionExceptions: {
            enable: this.valueT.uiSettings.deviceControl.dataLossPreventionExceptions.enable,
            rules: this.valueT.uiSettings.deviceControl.dataLossPreventionExceptions.rules,
          },
        },
      },
    };
  }

  onToggleChange(event: CustomEvent) {
    toggleDisabled(this.exclusionsContent, !event.detail.checked);

    // enable/disable grid empty state button
    const gridEmptyStateButton =
      this.exclusionsGridFormField.Input.shadowRoot?.getElementById('policies-grid-empty-state');
    if (event.detail.checked) {
      gridEmptyStateButton?.classList.remove('disabled');
    } else {
      gridEmptyStateButton?.classList.add('disabled');
    }
  }

  /**
   * Override reset method, because we need to reset also grid component.
   */
  public reset() {
    super.reset();
    this.exclusionsGridFormField.Input.reset();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_DEVICE_CONTROL_EXCLUSIONS: DeviceControlExclusionsWebComponent;
  }
}
